import axios from 'axios';

// Configuration de base d'Axios
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur de requête - ajoute le token admin si disponible
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur de réponse
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response) {
      console.error('Erreur serveur:', error.response.status, error.response.data);
      
      // Si token invalide ou expiré, déconnecter l'admin
      if (error.response.status === 401) {
        localStorage.removeItem('adminToken');
        window.location.href = '/admin/login';
      }
    } else if (error.request) {
      console.error('Pas de réponse du serveur:', error.request);
    } else {
      console.error('Erreur de configuration:', error.message);
    }
    return Promise.reject(error);
  }
);

// ========================================
// API ADMIN AUTH
// ========================================

/**
 * Créer un nouvel admin (Admin protégé)
 * @param {Object} data - {username, email, password}
 * @returns {Promise}
 */
export const registerAdmin = async (data) => {
  try {
    const response = await api.post('/admin/register', data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Login admin
 * @param {Object} credentials - {username, password}
 * @returns {Promise}
 */
export const loginAdmin = async (credentials) => {
  try {
    const response = await api.post('/admin/login', credentials);
    if (response.data.success) {
      localStorage.setItem('adminToken', response.data.data.token);
    }
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Logout admin
 */
export const logoutAdmin = () => {
  localStorage.removeItem('adminToken');
};

/**
 * Vérifier si admin est connecté
 * @returns {boolean}
 */
export const isAdminLoggedIn = () => {
  return !!localStorage.getItem('adminToken');
};

/**
 * Get admin profile
 * @returns {Promise}
 */
export const getAdminProfile = async () => {
  try {
    const response = await api.get('/admin/profile');
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ========================================
// API PERMANENCES (Événements)
// ========================================

/**
 * Créer une permanence (Admin protégé)
 */
export const createPermanence = async (permanenceData) => {
  try {
    const response = await api.post('/permanences', permanenceData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get toutes les permanences actives (Public)
 */
export const getAllPermanences = async () => {
  try {
    const response = await api.get('/permanences');
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get toutes les permanences (Admin)
 */
export const getAllPermanencesAdmin = async () => {
  try {
    const response = await api.get('/permanences/admin/all');
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get une permanence par ID
 */
export const getPermanenceById = async (id) => {
  try {
    const response = await api.get(`/permanences/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Mettre à jour une permanence (Admin)
 */
export const updatePermanence = async (id, data) => {
  try {
    const response = await api.put(`/permanences/${id}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Supprimer une permanence (Admin)
 */
export const deletePermanence = async (id) => {
  try {
    const response = await api.delete(`/permanences/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get disponibilités par créneau
 */
export const getDisponibilites = async (permanenceId) => {
  try {
    const response = await api.get(`/permanences/${permanenceId}/disponibilites`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ========================================
// API BÉNÉVOLES
// ========================================

/**
 * Créer une demande de bénévolat
 */
export const createBenevole = async (data) => {
  try {
    const response = await api.post('/benevoles', data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get tous les bénévoles (Admin)
 */
export const getAllBenevoles = async () => {
  try {
    const response = await api.get('/benevoles');
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Mettre à jour le statut d'un bénévole (Admin)
 */
export const updateBenevoleStatus = async (id, statut) => {
  try {
    const response = await api.put(`/benevoles/${id}/statut`, { statut });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Supprimer un bénévole (Admin)
 */
export const deleteBenevole = async (id) => {
  try {
    const response = await api.delete(`/benevoles/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ========================================
// API ACTUALITÉS
// ========================================

/**
 * Créer une actualité avec images multiples (Admin)
 */
export const createActualite = async (data) => {
  try {
    const formData = new FormData();
    formData.append('title', data.title);
    formData.append('description', data.description);
    formData.append('category', data.category);
    formData.append('type', data.type);
    formData.append('date', data.date);
    formData.append('tags', JSON.stringify(data.tags));
    formData.append('links', JSON.stringify(data.links || []));
    formData.append('eventDate', data.eventDate || '');

    // Plusieurs images
    if (data.images && data.images.length > 0) {
      data.images.forEach((file) => {
        if (file instanceof File) {
          formData.append('images', file);
        }
      });
    }

    const response = await api.post('/actualites', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Mettre à jour une actualité avec images multiples (Admin)
 */
export const updateActualite = async (id, data) => {
  try {
    const formData = new FormData();

    // Champs texte
    ['title', 'description', 'category', 'type', 'date', 'statut', 'eventDate'].forEach((key) => {
      if (data[key] !== undefined) {
        formData.append(key, data[key] || '');
      }
    });

    // Tags
    if (data.tags) {
      formData.append('tags', JSON.stringify(data.tags));
    }

    // Liens
    if (data.links !== undefined) {
      formData.append('links', JSON.stringify(data.links));
    }

    // Images existantes à garder (chemins serveur)
    if (data.existingImages) {
      formData.append('existingImages', JSON.stringify(data.existingImages));
    }

    // Nouvelles images (fichiers File)
    if (data.newImages && data.newImages.length > 0) {
      data.newImages.forEach((file) => {
        if (file instanceof File) {
          formData.append('images', file);
        }
      });
    }

    const response = await api.put(`/actualites/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get toutes les actualités (Public)
 */
export const getAllActualites = async () => {
  try {
    const response = await api.get('/actualites');
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get toutes les actualités (Admin)
 */
export const getAllActualitesAdmin = async () => {
  try {
    const response = await api.get('/actualites/admin/all');
    return response.data;
  } catch (error) {
    throw error;
  }
};


/**
 * Supprimer une actualité (Admin)
 */
export const deleteActualite = async (id) => {
  try {
    const response = await api.delete(`/actualites/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ========================================
// API CONTACT
// ========================================

/**
 * Créer un message de contact (Public)
 */
export const createContact = async (data) => {
  try {
    const response = await api.post('/contacts', data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get tous les messages de contact (Admin)
 */
export const getAllContacts = async () => {
  try {
    const response = await api.get('/contacts');
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Mettre à jour le statut d'un contact (Admin)
 */
export const updateContactStatut = async (id, statut) => {
  try {
    const response = await api.put(`/contacts/${id}/statut`, { statut });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Supprimer un contact (Admin)
 */
export const deleteContact = async (id) => {
  try {
    const response = await api.delete(`/contacts/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ========================================
// API PERMANENCE NUMÉRIQUE
// ========================================

/**
 * Créer une nouvelle demande
 * @param {Object} reservationData - Données de la demande
 * @returns {Promise}
 */
export const createReservation = async (reservationData) => {
  try {
    const response = await api.post('/permanence/reservation', reservationData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Récupérer toutes les demandes (Admin protégé)
 * @returns {Promise}
 */
export const getAllReservations = async () => {
  try {
    const response = await api.get('/permanence/reservations');
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Récupérer une demande par ID (Admin protégé)
 * @param {string} id - ID de la demande
 * @returns {Promise}
 */
export const getReservationById = async (id) => {
  try {
    const response = await api.get(`/permanence/reservation/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Mettre à jour le statut d'une demande (Admin protégé)
 * @param {string} id - ID de la demande
 * @param {string} statut - Nouveau statut (en_attente, contacte, traite, annule)
 * @returns {Promise}
 */
export const updateReservationStatus = async (id, statut) => {
  try {
    const response = await api.put(`/permanence/reservation/${id}`, { statut });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Supprimer une demande (Admin protégé)
 * @param {string} id - ID de la demande
 * @returns {Promise}
 */
export const deleteReservation = async (id) => {
  try {
    const response = await api.delete(`/permanence/reservation/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ========================================
// API BÉNÉVOLES (À implémenter)
// ========================================

export const submitBenevoleApplication = async (benevoleData) => {
  try {
    const response = await api.post('/benevoles', benevoleData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ========================================
// API CONTACT (À implémenter)
// ========================================

export const sendContactMessage = async (contactData) => {
  try {
    const response = await api.post('/contact', contactData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export default api;